/**
 * Copy editorial de la PROMOCIÓN — el texto que lee el jugador en el Centro.
 *
 * Tres textos, propiedad de la promoción (no de su recompensa):
 *   - cardTeaser       presentación breve en la card       (columna copy_card_teaser)
 *   - detailBody       explicación completa                 (columna descripcion)
 *   - completionBody   qué sucede al completar, opcional    (columna copy_completion_body)
 *
 * Vive en `shared/contracts/` porque es el CONTRATO entre el CRUD del Admin y
 * el proyector del Centro (`marketing-promotion-menu`): normalización, límites
 * y detección de marcado son UNA sola regla. Cuando cada lado tenía la suya,
 * lo almacenado, lo que mostraba el Admin y lo que proyectaba el runtime
 * podían ser tres textos distintos. El copy de la ceremonia de entrega es otro
 * contrato (`reward-presentation-copy.js`) y no se mezcla con este.
 *
 * Reglas que este contrato fija (los mensajes de error los redacta cada lado):
 *   - Solo texto plano: el marcado se detecta por PATRÓN de etiqueta ('<'
 *     pegado a letra, '/', '!' o '?'), no por carácter — "100% -> 2x" y
 *     "apuesta <5" son tipografía legítima.
 *   - Los caracteres de control no tienen representación legítima. VT y FF
 *     NO cuentan como whitespace normalizable: tanto \s+ como String.trim()
 *     los consumen, y un control escondido en la normalización jamás llega a
 *     la validación (medido: "U+000B + Oferta".trim() === "Oferta").
 *   - La normalización colapsa y recorta ÚNICAMENTE espacio, tab, CR y LF.
 *   - Vacío o solo whitespace canoniza a null: los tres campos son opcionales.
 */

export const LIMITES_COPY_EDITORIAL = Object.freeze({
    cardTeaser: 160,
    detailBody: 2000,
    completionBody: 1000,
});

export const CARACTERES_DE_CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;
export const CARACTERES_DE_CONTROL_GLOBAL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

// Etiqueta, no carácter suelto: '<b', '</i', '<!--', '<?xml' sí; '2 < 5' no.
export const PATRON_DE_MARCADO = /<[a-zA-Z/!?]/;

/**
 * Normaliza un texto editorial: colapsa el whitespace permitido a espacio
 * simple y recorta los bordes SIN tocar controles. Devuelve null si el texto
 * queda vacío (o no es string): null es la forma canónica de "sin copy".
 */
export function normalizarCopyEditorial(valor) {
    if (typeof valor !== 'string') return null;
    return valor.replace(/[ \t\r\n]+/g, ' ').replace(/^ +| +$/g, '') || null;
}

/**
 * Evalúa un texto YA normalizado contra las reglas del contrato.
 * Devuelve un veredicto neutro — cada consumidor redacta su propio mensaje:
 *   null        válido (incluye null: ausencia es válida)
 *   'control'   contiene caracteres de control
 *   'marcado'   contiene patrón de etiqueta
 *   'longitud'  excede el límite indicado
 */
export function evaluarCopyEditorial(valor, limite) {
    if (valor === null || valor === undefined) return null;
    if (typeof valor !== 'string') return 'control';
    if (CARACTERES_DE_CONTROL.test(valor)) return 'control';
    if (PATRON_DE_MARCADO.test(valor)) return 'marcado';
    if (valor.length > limite) return 'longitud';
    return null;
}

const CopyEditorialDePromocionContract = Object.freeze({
    LIMITES_COPY_EDITORIAL,
    CARACTERES_DE_CONTROL,
    CARACTERES_DE_CONTROL_GLOBAL,
    PATRON_DE_MARCADO,
    normalizarCopyEditorial,
    evaluarCopyEditorial,
});

export default CopyEditorialDePromocionContract;

if (typeof module !== 'undefined' && module.exports) module.exports = CopyEditorialDePromocionContract;
